import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, NEVER, of, tap } from 'rxjs';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';

import { filterDefined } from '@core/common-legacy';
import {
  EnabledIntegrationKindsActions,
  EnabledIntegrationKindsSelectors,
  IntegrationAbilitiesActions,
  IntegrationAbilitiesSelectors,
} from '@core/integrations-data';
import type { IntegrationAbilityInfo } from '@core/integrations-http-legacy';
import { IntegrationKinds } from '@core/integrations-types';

type InsertConfig = { insert: 'integrations-kununu'; requiredAbility: 'kununu_send' };
const INTEGRATION_REDACTOR_INSERT_MAP: Partial<Record<IntegrationKinds, InsertConfig | undefined>> =
  {
    [IntegrationKinds.KUNUNU]: { insert: 'integrations-kununu', requiredAbility: 'kununu_send' },
  };

@Injectable({ providedIn: 'root' })
export class IntegrationsInsertsService {
  private readonly store = inject(Store);

  public loadIntegrationInserts(): Observable<Array<InsertConfig['insert']>> {
    return this.getEnabledIntegrationKids().pipe(
      switchMap(kinds => {
        const kindsThatRequireAbilityCheck = kinds.filter(
          kind => !!INTEGRATION_REDACTOR_INSERT_MAP[kind]?.requiredAbility,
        );

        return this.getIntegrationAbilities(kindsThatRequireAbilityCheck).pipe(
          map(integrationAbilities =>
            kinds
              .filter(kind => this.isInsertableIntegration(kind, integrationAbilities))
              .map(kind => INTEGRATION_REDACTOR_INSERT_MAP[kind]!.insert),
          ),
        );
      }),
    );
  }

  public getEnabledIntegrationKids(): Observable<IntegrationKinds[]> {
    return combineLatest([
      this.store.select(EnabledIntegrationKindsSelectors.isLoaded),
      this.store.select(EnabledIntegrationKindsSelectors.isPending),
    ]).pipe(
      debounceTime(0),
      tap(([isLoaded, isPending]) => {
        if (isLoaded || isPending) return;

        this.store.dispatch(EnabledIntegrationKindsActions.fetch());
      }),
      filter(([isLoaded, isPending]) => isLoaded && !isPending),
      switchMap(() => this.store.select(EnabledIntegrationKindsSelectors.data)),
      filterDefined,
    );
  }

  private getIntegrationAbilities(kinds: string[]): Observable<IntegrationAbilityInfo[]> {
    if (!kinds.length) return of([]);

    this.store.dispatch(IntegrationAbilitiesActions.fetch({ payload: { kinds } }));
    return this.store.select(IntegrationAbilitiesSelectors.isPending).pipe(
      debounceTime(0),
      switchMap(isPending =>
        isPending
          ? NEVER
          : this.store.select(IntegrationAbilitiesSelectors.data).pipe(filterDefined),
      ),
    );
  }

  private isInsertableIntegration(
    kind: IntegrationKinds,
    integrationAbilities: IntegrationAbilityInfo[],
  ): boolean {
    const insertConfig = INTEGRATION_REDACTOR_INSERT_MAP[kind];

    // No insert config defined for integration
    if (!insertConfig) return false;

    // Insert config defined and no ability required to use it
    if (!insertConfig.requiredAbility) return true;

    // Insert config defined, but has required abilities
    const abilityInfo = integrationAbilities.find(
      integrationAbilityInfo => integrationAbilityInfo.kind === kind,
    );
    return !!abilityInfo && abilityInfo.abilities.includes(insertConfig.requiredAbility);
  }
}
